import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AdministrativeAreaTreeSelect } from "@/components/AdministrativeAreaTreeSelect";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { getErrors as getServerFieldErrors } from "../../../../../../utils";
import { aggregatorLanguages } from "../../../../../../utils/i18n";
import { TraderDetailsEligibleAggregatorsField } from "../TraderDetailsEligibleAggregatorsField";
import { TraderDetailsLanguageField } from "../TraderDetailsLanguageField";
import { TraderDetailsNameField } from "../TraderDetailsNameField";
import { TraderDetailsPhysicalAddressField } from "../TraderDetailsPhysicalAddressField";
import { TraderDetailsProfileField } from "../TraderDetailsProfileField";
import { TraderDetailsTraderTypeField } from "../TraderDetailsTraderTypeField";
import { TraderDetailsVendorFields } from "../TraderDetailsVendorFields";
import type {
  SelectOption,
  TraderDetailsProfileFieldName,
  TraderDetailsStepOneProps,
} from "./types";
import {
  createLanguageOptions,
  createTraderTypeOptions,
  getLocationLevelPlaceholder,
  toErrorMessage,
  TRADER_TYPE_LABEL_KEYS,
  TRADER_TYPES,
} from "./utils";

export type { TraderDetailsStepOneFormValues } from "./types";
export { createTraderDetailsStepOneSchema } from "./schema";

export const TraderDetailsStepOne = ({
  methods,
  administrativeAreas = [],
  showBlankFieldErrors,
  isSaving = false,
  fieldErrors = {},
  aggregatorOptions = [],
  isFetchingAggregators = false,
}: TraderDetailsStepOneProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
  } = methods;

  const locationValue = watch("location");
  const isWfpVendorValue = watch("is_wfp_vendor");
  const traderTypeValue = watch("trader_type");
  const isSchoolBuyer = traderTypeValue?.value === TRADER_TYPES.school;
  // Translated from the value: when editing, the option label holds the i18n key.
  const traderTypeLabelKey = TRADER_TYPE_LABEL_KEYS[String(traderTypeValue?.value)];
  const traderTypeLabel = traderTypeLabelKey ? t(traderTypeLabelKey) : "";

  const languageOptions = useMemo<SelectOption[]>(
    () => createLanguageOptions(aggregatorLanguages),
    []
  );

  const traderTypeOptions = useMemo<SelectOption[]>(() => createTraderTypeOptions(t), [t]);

  const getFieldError = (fieldName: TraderDetailsProfileFieldName) => {
    const serverSideError = toErrorMessage(getServerFieldErrors(fieldErrors, fieldName));
    if (serverSideError) {
      return serverSideError;
    }

    if (!showBlankFieldErrors) {
      return "";
    }

    const error = errors[fieldName] as { message?: unknown } | string | undefined;
    return toErrorMessage(error);
  };

  return (
    <Card
      aria-busy={isSaving}
      className="relative mb-4 overflow-hidden border-neutral-alpha-200 bg-background shadow-none"
    >
      <CardContent className={cn("space-y-6", isSaving && "opacity-60")}>
        <TraderDetailsNameField
          control={control}
          label={t("traderDetails.form.name")}
          errorMessage={getFieldError("name")}
          isDisabled={isSaving}
        />

        <TraderDetailsTraderTypeField
          control={control}
          label={t("traderDetails.form.traderType")}
          options={traderTypeOptions}
          placeholder={t("traderDetails.form.traderTypePlaceholder")}
          errorMessage={getFieldError("trader_type")}
          isDisabled={isSaving}
        />

        {isSchoolBuyer && (
          <TraderDetailsEligibleAggregatorsField
            control={control}
            label={t("traderDetails.form.eligibleAggregatorsLabel", {
              buyerType: traderTypeLabel,
            })}
            tooltip={t("traderDetails.form.eligibleAggregatorsHint")}
            placeholder={t("traderDetails.form.eligibleAggregatorsPlaceholder")}
            searchPlaceholder={t("traderDetails.form.eligibleAggregatorsSearch")}
            emptyMessage={t("traderDetails.form.eligibleAggregatorsEmpty")}
            options={aggregatorOptions}
            isLoading={isFetchingAggregators}
            isDisabled={isSaving}
            errorMessage={getFieldError("eligible_aggregators")}
          />
        )}

        <TraderDetailsLanguageField
          control={control}
          label={t("aggregatorDetails.form.language")}
          options={languageOptions}
          placeholder={t("traderDetails.form.primaryLanguage")}
          errorMessage={getFieldError("language")}
          isDisabled={isSaving}
        />

        <Controller
          control={control}
          name="location"
          rules={{ required: true }}
          render={({ field }) => {
            const errorMessage = getFieldError("location");
            return (
              <TraderDetailsProfileField
                id="location-treePicker"
                label={t("traderDetails.form.administrativeArea")}
                tooltip={t("traderDetails.form.locationHintTooltip")}
                errorMessage={errorMessage}
                required
              >
                <AdministrativeAreaTreeSelect
                  id="location-treePicker"
                  value={field.value}
                  tree={administrativeAreas}
                  placeholder={getLocationLevelPlaceholder(locationValue, t)}
                  clearLabel={t("common.clearAll")}
                  hasError={!!errorMessage}
                  isDisabled={isSaving}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              </TraderDetailsProfileField>
            );
          }}
        />

        <TraderDetailsPhysicalAddressField
          control={control}
          label={t("traderDetails.form.traderPhysicalAddress")}
          tooltip={t("traderDetails.form.traderPhysicalAddressInfoTip")}
          errorMessage={getFieldError("physical_address")}
          isDisabled={isSaving}
        />

        <TraderDetailsVendorFields
          control={control}
          getFieldError={getFieldError}
          isDisabled={isSaving}
          isWfpVendorValue={isWfpVendorValue}
          vendorLabel={t("traderDetails.form.isWfpVendor")}
          wingsIdLabel={t("traderDetails.form.wingsId")}
        />
      </CardContent>
      {isSaving && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
          <div className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3 text-sm font-medium text-muted-foreground shadow-sm">
            <Spinner className="size-5 text-primary" />
            <span>{t("loading.defaultMessage")}</span>
          </div>
        </div>
      )}
    </Card>
  );
};
